/*
 * An SVGO plugin to reorganize paths with multiple subpaths for PIXI
 *
 * If an SVG path command has more than one subpath (ie. multiple 'M' instructions) it's not clear
 * how to transform the path for PIXI. According to the SVG specification, subpaths "inside" other subpaths
 * may be drawn as "holes" -- or not! -- depending on a complicated set of criteria.
 *
 * (@see https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/fill-rule)
 *
 * Since it's hard to even find a clear definition of the anointed algorithms, we're trying a simpler heuristic:
 *
 * - If the subpaths don't (seem to) overlap, split each subpath into its own separate path
 * - Otherwise order the subpaths so the  "positive" polygon subpath goes first and the "holes" follow.
 *   (that is, consistent with the way PIXI renders "holes")
 *
 * To decide if subpaths overlap, we're using code from snapsvg that detects if a point intersects a polygon,
 * and checking if each point on one of the paths is inside another.
 *
 * This is imperfect since:
 *
 * - it's not clear what to do with *partially* overlapping subpaths (or even if we'll ever see such a thing)
 * - we're ignoring the control points that might push a polygon to *be* partially overlapping
 * - we're only looking one level deep (that is, ignoring "holes-within-holes")
 *
 * NOTE: The path must contain ONLY M, L and C instructions (@see simplify-path-to-only-mlc.js)
 *
 * Types
 *
 *   SvgoItem      = (as defined by SVGO)
 *   PathString    = String (pattern: /<SubpathString>+/)
 *   SubpathString = String (pattern: /M<Number>{2}|L<Number>{2}|C<Number>{6}/
 *
 *   ControlPointX = Number
 *   ControlPointY = Number
 *   EndPointX     = Number
 *   EntPointY     = Number
 *   Point         = [Number, Number]
 *   Cubic         = ['C', ControlPointX, ControlPointY, ControlPointX, ControlPointY, ExdPointX, ExdPointY]
 */
import { all, none, curry, identity, pipe, tail, map, takeLast, unnest } from 'ramda'

import Snap from '../snapsvg-lite'

const ALL  = 'all'
const SOME = 'some'
const NONE = 'none'

const SPLIT_SUBPATHS   = 'split'
const REVERSE_SUBPATHS = 'reverse'
const DO_NOTHING       = 'nothing'

const allPass  = all(identity)                                                       // :: [Boolean] -> Boolean
const nonePass = none(identity)                                                      // :: [Boolean] -> Boolean

const split           = curry((regex, s) => s.split(regex))                          // :: Regex -> String -> [String]
const prependToString = curry((prefix, s) => prefix + s)                             // :: String -> String -> String
const subpathStrings  = pipe(split(/M/ig), tail, map(prependToString('M')))          // :: PathString -> [SubpathString]

const cubicEndpoint   = takeLast(2)                                                  // :: Cubic -> Point
const isPointInside   = curry((cubic, p) => Snap.isPointInside(cubic, ...p))         // :: Cubic -> Point -> Boolean

const itemPathString     = item => item.attr('d').value                              // :: SvgoItem -> PathString
const itemSubpathStrings = pipe(itemPathString, subpathStrings)                      // :: SvgoItem -> [SubpathString]
const itemAsCubics       = pipe(itemSubpathStrings, map(Snap.toCubic))               // :: SvgoItem -> [Cubic]


/*
 * Are all, none or some of the points inside the cubic?
 * @sig calulcateInsideness :: (Cubic, [Point]) -> String
 */
const calulcateInsideness = (cubic, points) => {
    const results = map(isPointInside(cubic), points)

    if (allPass(results))  return ALL
    if (nonePass(results)) return NONE
    return SOME
}

/*
 * Given two closed cubic bezier curves, decide if each of the curves is entirely, partially or not all contained
 * within the other as a guide to what can be done to simplify the SVG path(s).
 *
 * NOTE: what we're actually doing is determining only if the individual points of the curves are within the other
 * because this is so much simpler than also dealing with the fact that the bezier curves mean that either curve
 * might go wildly outside the polygon defined by just the endpoints.
 *
 * @sig disposition :: SvgoItem -> String
 */
const disposition = item => {
    const [cubicA, cubicB] = itemAsCubics(item)

    const endPointsA = map(cubicEndpoint, cubicA)
    const endPointsB = map(cubicEndpoint, cubicB)

    const bInsideA = calulcateInsideness(cubicA, endPointsB)
    const aInsideB = calulcateInsideness(cubicB, endPointsA)

    // reverse or split when it seems clear
    if (bInsideA === NONE && aInsideB === ALL)  return REVERSE_SUBPATHS
    if (bInsideA === NONE && aInsideB === SOME) return REVERSE_SUBPATHS
    if (bInsideA === NONE && aInsideB === NONE) return SPLIT_SUBPATHS

    // leave these alone for now
    if (bInsideA === ALL  && aInsideB === NONE) return DO_NOTHING
    if (bInsideA === SOME && aInsideB === NONE) return DO_NOTHING
    if (bInsideA === ALL  && aInsideB === SOME) return DO_NOTHING
    if (bInsideA === SOME && aInsideB === ALL)  return DO_NOTHING
    if (bInsideA === SOME && aInsideB === SOME) return DO_NOTHING
    if (bInsideA === ALL  && aInsideB === ALL)  return DO_NOTHING
}

/*
 * An SVG path allows multiple polygons in a subpath, but in PIXI JSON, any polygon after the first is a hole.
 * If we can determine that the subpaths don't overlap, we can sepaate each subpath into its own path
 * so both end up visible in PIXI JSON
 * @sig splitSubpaths :: SvgoItem -> [SvgoItem]
 */
const splitSubpaths = item1 => {
    console.log(`  simplify-subpaths: splitting an SVG path to prevent mistakenly creating a "hole" in a polygon`)

    const subpaths = itemSubpathStrings(item1)
    const item2    = item1.clone()

    item1.addAttr({ name: 'd', value: subpaths[0], prefix: '', local: 'd' })
    item2.addAttr({ name: 'd', value: subpaths[1], prefix: '', local: 'd' })

    return [item1, item2]
}

/*
 * An SVG path can define a polygon and hole in either order, but PIXI JSON requires the hole to follow the polygon
 * (We'll only get here if they're in the wrong order.)
 * NOTE: we're only dealing with exactly two subpaths for now
 * :: (SvgoItem, String) -> SvgoItem
 */
const reverseSubpaths = item => {
    console.log(`  simplify-subpaths: reversing the subpaths of an SVG path to put the "hole" *after* the polygon`)
    const subpaths = itemSubpathStrings(item)
    const path     = subpaths[1] + subpaths[0]

    item.addAttr({ name: 'd', value: path, prefix: '', local: 'd' })

    return item
}

/*
 * Dispatch an SVG path for processing depending on what action seems warranted
 * @sig processPathWithTwoSubpaths :: SvgoItem -> SvgoItem|[SvgoItem]
 */
const processPathWithTwoSubpaths = item => {
    switch(disposition(item)) {
    case DO_NOTHING:       return item
    case SPLIT_SUBPATHS:   return splitSubpaths(item)
    case REVERSE_SUBPATHS: return reverseSubpaths(item)
    default:
        console.log(`  simplify-subpaths: ignoring SVG path: not sure what to do`)
        return item
    }
}

/*
 * Process an SVG 'path'
 * NOTE: right now we only handle paths with exactly two subpaths
 * @sig processPathItem :: SvgoItem -> SvgoItem|[SvgoItem]
 */
const processPathItem = item => {
    switch (itemSubpathStrings(item).length) {
    case 1:  return item
    case 2:  return processPathWithTwoSubpaths(item)
    default:
        console.log(`  simplify-subpaths: leaving an SVG path with more than two subpaths unchanged`)
        return item
    }
}

/*
 * Depth-first recursive tree-node processor that uses an item's 'content' as children
 * The only real processing happens for 'path' items
 * @sig SvgoItem -> SvgoItem
 */
const recurse = item => {
    if (item.content) item.content = unnest(map(recurse, item.content))
    if (item.elem === 'path') item = processPathItem(item)
    return item
}

/*
 * Main SVGO plugin descriptor
 *
 * NOTE: This plugin uses SVGO's 'full' type (rather than 'perItem') because the algorithm sometimes *adds* nodes to the
 * item tree and it seems like managing the entire tree is the only way to do this. That means that 'fn' has to
 * return the whole tree -- possibly transformed.
 */
const fixSubpathsForPixi = {
    type       : 'full',
    active     : true,
    description: 'Reorganize paths with multiple subpaths for PIXI',
    params     : {},
    fn         : recurse,
}


export { fixSubpathsForPixi }



